import React, { useEffect, useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

/* Atoms */
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'

/* Molecules */
import SidebarDialog from 'components_new/molecules/SidebarDialog'
import TabSwitch from 'components_new/molecules/TabSwitch'

/* Organisms */
import AdminList from 'components_new/organisms/AdminList'
import DashboardPermissions from 'components_new/organisms/DashboardPermissions'
import DashboardCompanyGroupPermissions from 'components_new/organisms/DashboardPermissions/CompanyGroups'
import PrivateKpiStatusBox from 'components_new/organisms/PrivateKpiStatusBox'

import Actions from './Actions'
import InviteSection from './InviteSection'
import Mobile from './mobile'
import Title from './Title'

/* Actions */
import { useDashboardGroupActions } from 'redux/actions/DashboardGroups'

/* Hooks */
import { useIsManagementCompany } from 'redux/hooks/Customer'
import { useAllowEditDashboardGroup } from 'redux/hooks/Permissions'
import { useIsMobile } from 'utils/hooks'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'

/* Types */
import { TDashboard } from 'types/GlobalDashboard'
import { TDashboardGroup } from 'types/GlobalDashboardGroup'

enum SwitchTabValues {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL'
}

const translateSwitchTabValues: Record<
  SwitchTabValues,
  'internal' | 'external'
> = {
  [SwitchTabValues.INTERNAL]: 'internal',
  [SwitchTabValues.EXTERNAL]: 'external'
}

interface ShareDashboardDialogProps {
  dashboard: TDashboard | null
  dashboardGroup: TDashboardGroup | null
  onClose: () => void
  onOpen: () => void
  open: boolean
}

const ShareDashboardDialog = (props: ShareDashboardDialogProps) => {
  const { dashboard, dashboardGroup, onClose, onOpen, open } = props

  const {
    tryInviteToDashboard,
    tryRevokeFromDashboard,
    tryUpdateDashboardGroup
  } = useDashboardGroupActions()

  const { t } = useTranslation()

  const allowEditDashboardGroup = useAllowEditDashboardGroup(dashboardGroup?.id)
  const isManagementCompany = useIsManagementCompany()
  const { isAdmin } = useAccountSettings()
  const isMobile = useIsMobile()

  const [tab, setTab] = useState<'default' | 'email'>('default')
  const [switchTab, setSwitchTab] = useState<SwitchTabValues>(
    SwitchTabValues.INTERNAL
  )

  useEffect(() => {
    if (!open) {
      setTab('default')
    }
  }, [open])

  if (!dashboard || !dashboardGroup) return null

  const isPublic = dashboard.share_settings.is_public
  const publicLink = dashboard.share_settings.public_link

  if (isMobile)
    return (
      <Mobile
        dashboard={dashboard}
        dashboardGroup={dashboardGroup}
        onClose={onClose}
        onOpen={onOpen}
        open={open}
        title={t('global', 'share_name', false, {
          name: dashboard.title ?? ''
        })}
      />
    )

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SidebarDialog
        actions={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1
              }}
            >
              {tab === 'default' ? (
                <Actions
                  isPublic={isPublic}
                  onClose={onClose}
                  publicLink={publicLink}
                  setTab={(value) => setTab(value)}
                  tab={tab}
                  showActions={switchTab === SwitchTabValues.INTERNAL}
                />
              ) : undefined}
            </Box>
            <Button onClick={onClose} variant="text">
              {t('global', 'close')}
            </Button>
          </Box>
        }
        onClose={onClose}
        open={open}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 'fit-content'
          }
        }}
        title={<Title groupTitle={dashboard.title} setTab={setTab} tab={tab} />}
      >
        <Collapse
          in={
            tab === 'default' && isManagementCompany && allowEditDashboardGroup
          }
          unmountOnExit
        >
          <TabSwitch
            options={Object.values(SwitchTabValues).map((value) => ({
              value,
              label: t('global', translateSwitchTabValues[value])
            }))}
            value={switchTab}
            onChange={(value) => setSwitchTab(value as SwitchTabValues)}
            color={'primary'}
            sx={{ mt: -2, mb: 2 }}
          />
        </Collapse>
        <Collapse in={switchTab === SwitchTabValues.EXTERNAL} unmountOnExit>
          <DashboardCompanyGroupPermissions
            dashboardGroup={dashboardGroup}
            onInvite={(companyGroupId: string) =>
              tryInviteToDashboard(dashboardGroup.id, {
                company_group_id: companyGroupId
              })
            }
            onRemove={(companyGroupId: string) =>
              tryRevokeFromDashboard(dashboardGroup.id, {
                company_group_id: companyGroupId
              })
            }
            setAllCompanyGroupsHasAccess={(allCompanyGroupsHasAccess) => {
              tryUpdateDashboardGroup(dashboardGroup.id, {
                all_company_groups_has_access: allCompanyGroupsHasAccess
              })
            }}
          />
        </Collapse>
        <Collapse in={switchTab === SwitchTabValues.INTERNAL} unmountOnExit>
          <Box>
            <PrivateKpiStatusBox id={dashboardGroup.id} />
            <Collapse in={tab === 'default'}>
              <DashboardPermissions
                dashboardGroup={dashboardGroup}
                onInvite={(permission) =>
                  tryInviteToDashboard(dashboardGroup.id, permission)
                }
                revokeAccess={(body) =>
                  tryRevokeFromDashboard(dashboardGroup.id, body)
                }
                setWhoCanView={(whoCanView) =>
                  tryUpdateDashboardGroup(dashboardGroup.id, {
                    permission_type: whoCanView
                  })
                }
              />
            </Collapse>
            {!isAdmin ? (
              <AdminList
                collapse={{
                  active: true,
                  defaultCollapsed: true
                }}
                header={{
                  title: t('users', 'cant_find_user')
                }}
                helperText={t('users', 'contact_system_admin')}
                size="small"
              />
            ) : null}
            <Collapse in={tab === 'email'}>
              <InviteSection
                id={dashboardGroup.id}
                onGoBack={() => setTab('default')}
              />
            </Collapse>
          </Box>
        </Collapse>
      </SidebarDialog>
    </ThemeProvider>
  )
}

export default ShareDashboardDialog
